import React from 'react';

import { createStyles, Theme, WithStyles, withStyles, } from '@material-ui/core/styles';
import Input from '@material-ui/core/Input';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Tooltip from '@material-ui/core/Tooltip';
import FormControl from '@material-ui/core/FormControl';

import locales from '../../../locales';
import { IFreeDay, IScheduleRow, TWeekDay, } from '../../../types/IScheduleStore';
import { sortWeekdays } from '../../../helpers/scheduleHelpers';

import NewTrashIcon from "../Icons/NewTrashIcon.svg";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import classNames from "classnames";

const styles = (theme: Theme) => createStyles({
  formControl: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'minmax(auto, 310px) 100px 100px 46px;',
    [theme.breakpoints.down(600)]: {
      gridTemplateColumns: "repeat(5, 1fr)",
      gridTemplateRows: "repeat(2, 1fr)",
      gap: "8px"
    },
    alignItems: 'center',
    marginBottom: '16px',
    columnGap: '10px',
    '& .MuiInput-underline:before': {
      borderBottom: 'none !important'
    },
    '& .MuiInput-underline:after': {
      borderBottom: 'none !important'
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottom: 'none !important'
    },
    '& .MuiSelect-select': {
      height: '20px',
      boxSizing: 'border-box',
      lineHeight: '20px',
      padding: '0 12px'
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: '#fff'
    },
    '& .MuiSelect-icon': {
      marginRight: '16px'
    }
  },
  select: {
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '8px 12px',
    '&:focus': {
      borderRadius: '4px',
      backgroundColor: '#fff',
    },
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  noLabel: {
    marginTop: theme.spacing(3),
  },
  timeFields: {
    width: 105,
    [theme.breakpoints.down(600)]: {
      width: "100%",
    },
  },
  iconBtn: {
    cursor: 'pointer',
    marginLeft: 8,
    display: 'flex',
    backgroundColor: '#8A8D93',
    alignItems: 'center',
    justifyContent: 'center',
    width: '37px',
    height: '37px',
    borderRadius: '4px'
  },
  icon: {
    width: '24px',
    height: '24px'
  },

  days: {
    [theme.breakpoints.down(600)]: {
      gridColumn: "span 5 / span 5"
    },
  },
  startTime: {
    [theme.breakpoints.down(600)]: {
      gridColumn: "span 2 / span 2",
      gridRowStart: 2
    },
  },
  endTime: {
    [theme.breakpoints.down(600)]: {
      gridColumn: "span 2 / span 2",
      gridColumnStart: 3,
      gridRowStart: 2
    },
  },
  delete: {
    [theme.breakpoints.down(600)]: {
      gridColumnStart: 5,
      gridRowStart: 2,
      display: "flex",
      justifyContent: "flex-end"
    },
  },
  menuItem: {
    '&.Mui-disabled': {
      color: theme.palette.secondary.main
    }
  }
});

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface DayInfo {
  abbreviation: string;
  name: string;
}

const days: { [key: string]: DayInfo } = {
  'MONDAY': { abbreviation: 'Пн', name: 'Понедельник' },
  'TUESDAY': { abbreviation: 'Вт', name: 'Вторник' },
  'WEDNESDAY': { abbreviation: 'Ср', name: 'Среда' },
  'THURSDAY': { abbreviation: 'Чт', name: 'Четверг' },
  'FRIDAY': { abbreviation: 'Пт', name: 'Пятница' },
  'SATURDAY': { abbreviation: 'Сб', name: 'Суббота' },
  'SUNDAY': { abbreviation: 'Вс', name: 'Воскресенье' },
}

interface IProps extends WithStyles<typeof styles> {
  onChange: (value: string | Array<string> | unknown, type: 'start' | 'end' | 'days') => void;
  onDelete: () => void;
  schedule: IScheduleRow;
  freeDays: Array<IFreeDay>;
  state: {
    days: Array<TWeekDay>;
    start: string;
    end: string;
  };
  workTimes: Array<string>;
}

const ScheduleRow: React.FC<IProps> = (props) => {
  const {
    classes,
    schedule,
    freeDays,
    state,
    onChange,
    onDelete,
    workTimes,
  } = props;

  return (
    <div className={classes.formControl}>
      <FormControl error={state.days.length === 0 || schedule.error} fullWidth  className={classes.days}>
        <Select
          className={classes.select}
          multiple
          value={state.days}
          onChange={(e) => onChange(e.target.value, "days")}
          input={<Input id="select-multiple-chip" />}
          renderValue={(selected) => (
            <div className={classes.chips}>
              {sortWeekdays(selected as string[]) // сортируем список дней
              .map((day: string, index: number) => (
                <span key={day + index} style={{ marginRight: 5 }}>
                    {index !== (selected as string[]).length - 1 // если индекс элемента меньше длины, ставим запятую
                      ? `${locales.t(`common.scheduleDays.${day}`)},` // - здесь ставим запятую после дня
                      : locales.t(`common.scheduleDays.${day}`)}
                  </span>
              ))}
            </div>
          )}
          IconComponent={(props) =>
            props.open ? <ExpandLess {...props} /> : <ExpandMore {...props} />
          }
          inputProps={{
            name: "days",
          }}
          MenuProps={MenuProps}
        >
          {freeDays.map((day) => (
            <MenuItem
              disabled={!day.free && state.days.indexOf(day.day) === -1}
              key={day.day}
              value={day.day}
              className={classes.menuItem}
            >
              {days[day.day].name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl disabled={workTimes.length === 0} className={classes.startTime}>
        <Select
          error={schedule.error}
          className={classNames(classes.timeFields, classes.select)}
          value={state.start}
          name="start"
          IconComponent={(props) =>
            props.open ? <ExpandLess {...props} /> : <ExpandMore {...props} />
          }
          onChange={(e) => onChange(e.target.value, "start")}
        >
          {workTimes.map((time) => (
            <MenuItem key={time} value={time}>
              {time}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl disabled={workTimes.length === 0} className={classes.endTime}>
        <Select
          error={schedule.error}
          className={classNames(classes.timeFields, classes.select)}
          name="end"
          value={state.end}
          IconComponent={(props) =>
            props.open ? <ExpandLess {...props} /> : <ExpandMore {...props} />
          }
          onChange={(e) => onChange(e.target.value, "end")}
        >
          {workTimes.map((time) => (
            <MenuItem key={time} value={time === "00:00" ? "23:59" : time}>
              {time === "23:59" ? "00:00" : time}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <div className={classes.delete}>
        <Tooltip title="Удалить">
          <div className={classes.iconBtn} onClick={onDelete}>
            <img className={classes.icon} src={NewTrashIcon} alt="logo" />
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

export default withStyles(styles, { withTheme: true })(ScheduleRow);
